import { useCallback, useState } from 'react';
import { useProjectRequestDownloadTokenLazyQuery } from '../types/graphqlTypes';

// Kick off a download for a SAS-signed Azure URL via a hidden iframe so the browser fetches the
// file directly from blob storage without navigating away from the page (which would tear down the
// WebGL context). The iframe is cleaned up after a minute, by which point the download has started.
export const triggerUrlDownload = (url: string) => {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;
  document.body.appendChild(iframe);
  setTimeout(() => document.body.removeChild(iframe), 60000);
};

export const useDownload = () => {
  const [getDownloadToken] = useProjectRequestDownloadTokenLazyQuery();
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);

  const downloadOriginalFile = useCallback(async (projectId: string, pointcloudId: string) => {
    if (!projectId) return;
    setDownloadProgress(0);
    try {
      const { data: tokenData } = await getDownloadToken({ variables: { projectId } });
      const response = await fetch(`${import.meta.env.VITE_NX_SERVER}/project/${projectId}/pointcloud/${pointcloudId}/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tokenData?.projectRequestDownloadToken }),
      });

      // The server returns SAS-signed Azure URLs ({ files: [{ url, fileName }] }); each file is
      // downloaded straight from blob storage. Stagger the triggers so browsers don't drop
      // simultaneous downloads (some block all but the first when fired in the same tick).
      const { files } = (await response.json()) as { files?: { url: string; fileName: string }[] };
      const fileList = files ?? [];
      for (let i = 0; i < fileList.length; i++) {
        triggerUrlDownload(fileList[i].url);
        if (i < fileList.length - 1) await new Promise((resolve) => setTimeout(resolve, 500));
      }
    } catch (e) {
      console.error('download failed:', e);
    } finally {
      setDownloadProgress(null);
    }
  }, [getDownloadToken]);

  return { downloadOriginalFile, downloadProgress };
};
